import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { globalQueryKeys, useReadContent } from '@opallabs/api'
import { content } from '@opallabs/api/v3'
import { type CalendarItem, type ResolveImageUrl } from '../types'

/**
 * Starts reading a content item's snapshot before its panel is open, so the
 * panel finds it waiting rather than showing a loader where the picture goes.
 *
 * It is keyed the way `usePanelSnapshot` reads, so the one answers the other.
 * Anything that is not content has no snapshot to read. React Query holds a
 * fresh answer for the workspace's default staleness, so resting on the same
 * chip twice reads once.
 */
export const usePrefetchSnapshot = (): ((item?: CalendarItem) => void) => {
  const queryClient = useQueryClient()

  return useCallback(
    (item?: CalendarItem) => {
      if (item?.kind !== 'content') return

      void queryClient.prefetchQuery({
        queryKey: globalQueryKeys.contentByIdV3(item.id),
        queryFn: () =>
          content.readSingle({ path: { content_id: item.id }, query: {} }),
      })
    },
    [queryClient]
  )
}

/**
 * The picture of the item a panel is showing, fetched one item at a time.
 *
 * The month payload carries no pictures: a workspace month holds hundreds of
 * content items and a panel shows one. So the snapshot is read only once a panel
 * is open, and only for content — nothing else has one. React Query keeps what
 * it read, so stepping back to an item already seen draws it straight away.
 *
 * Everything the panel will never fetch for is answered `null` rather than
 * `undefined`, so the panel draws no picture area instead of waiting for ever.
 */
export const usePanelSnapshot = (item?: CalendarItem): ResolveImageUrl => {
  const contentId = item?.kind === 'content' ? item.id : undefined

  const { data: imageUrl, isError } = useReadContent({
    contentId,
    select: (document) => document.data.attributes.default_snapshot_url ?? null,
  })

  return useCallback(
    (each: CalendarItem) => {
      // The answer is keyed to the item that was asked for. Anything else is an
      // item this hook is not reading, including the one the panel has just
      // left behind.
      if (each.id !== contentId) return null
      return isError ? null : imageUrl
    },
    [contentId, imageUrl, isError]
  )
}
